/**
 * Quick view of one device: status, newest pressure / temperature / battery reading, how
 * current the data is and the next check (tri-server#1364). Read-only — a look, not an
 * inspection.
 *
 * Shown at the top of the floor plan's action menu. `AssetQuickView` reads through
 * `useAssetStatusSummary`, so where the plan already loaded the device no extra request is
 * made. `AssetQuickViewBody` is the pure rendering, for tests and callers that hold the
 * summary themselves. `AssetStatusCell` / `AssetReadingCell` are the table forms.
 *
 * Old data is never shown as current: an offline device says since when, a critical / warning
 * / normal on old data keeps its badge but carries a "Stand … – veraltet" hint, and its values
 * render muted as "zuletzt gemessen".
 */

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import type { AssetReading, AssetStatusSummary } from "../AssetStatusAPI";
import { useAssetStatusSummary } from "../hooks/useAssetStatusSummary";
import {
  DUE_STATE_CLASSES,
  describeStatus,
  differsByMoreThanAnHour,
  dueState,
  formatDate,
  formatDateTime,
  formatReading,
  formatRelativeTime,
  formatSince,
  triggerMetric,
  type StatusView,
} from "../utilities/assetStatus";
import { AssetStatusBadge, StaleHint, useStatusReasonText } from "./AssetStatusBadge";

const DASH = "—";

/** Reasons the freshness line already says are not repeated next to the badge. */
const REASONS_SAID_BY_FRESHNESS = new Set(["noUplinkSince", "noUplink"]);

function useLanguage() {
  const { i18n } = useTranslation();
  return i18n?.language || "de";
}

// i18next's TFunction is wider than a plain string function; the texts here are plain strings.
type Translate = (key: string, values?: any) => any;

/**
 * The one line about how current the data is.
 * - offline: "Offline seit 24.09., 07:31"
 * - stale: "Stand 24.09., 07:31 – veraltet" (short: "Stand 24.09., 07:31")
 * - never sent: "Noch keine Daten empfangen"
 * - current: "Aktualisiert vor 12 Min." (short: "vor 12 Min.")
 */
export function freshnessLine(
  t: Translate,
  view: StatusView,
  language: string,
  now: number,
  short = false,
): { text: string; stale: boolean } | undefined {
  if (view.display === "offline") {
    const since = formatSince(view.lastSeenAt, language, now);
    return { text: since ? t("assetQuickView.offlineSince", { time: since }) : t("assetQuickView.status.offline"), stale: true };
  }
  if (view.stale) {
    const at = formatDateTime(view.lastSeenAt, language, now);
    if (!at) return { text: t("assetQuickView.staleUnknown"), stale: true };
    return { text: t(short ? "assetQuickView.staleAtShort" : "assetQuickView.staleAt", { time: at }), stale: true };
  }
  const ago = formatRelativeTime(view.lastSeenAt, language, now);
  if (!ago) return view.display === "noData" ? { text: t("assetQuickView.noUplink"), stale: false } : undefined;
  return { text: short ? ago : t("assetQuickView.updated", { time: ago }), stale: false };
}

function Stat({
  label,
  value,
  extra,
  age,
  muted,
  alert,
  testId,
}: {
  label: string;
  value?: string;
  extra?: string;
  age?: string;
  muted?: boolean;
  alert?: boolean;
  testId: string;
}) {
  return (
    <div data-testid={testId} data-muted={muted ? "true" : undefined} className="flex min-w-0 flex-col">
      <dt className="truncate text-[11px] leading-4 text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          "truncate text-sm font-medium leading-5 tabular-nums",
          muted ? "font-normal text-muted-foreground" : alert ? "text-destructive" : "text-foreground",
        )}
      >
        {value ?? <span className="text-muted-foreground">{DASH}</span>}
        {extra ? <span className="ml-1 text-[11px] font-normal text-muted-foreground">{extra}</span> : null}
      </dd>
      {age ? (
        <span data-testid={`${testId}-age`} className="truncate text-[10px] leading-3 text-muted-foreground">
          {age}
        </span>
      ) : null}
    </div>
  );
}

interface BodyProperties {
  summary?: AssetStatusSummary | null;
  loading?: boolean;
  error?: unknown;
  /** Header text; defaults to the device id from the summary. */
  title?: ReactNode;
  /** Reference time for relative ages and the due state; defaults to now. */
  now?: number;
  className?: string;
}

export function AssetQuickViewBody({ summary, loading, error, title, now, className }: BodyProperties) {
  const { t } = useTranslation();
  const language = useLanguage();
  const reasonText = useStatusReasonText();
  const reference = now ?? Date.now();
  const heading = title ?? summary?.id;

  const header = (badge: ReactNode, reason?: string) => (
    <div className="flex min-w-0 items-center gap-2">
      {heading ? (
        <span data-testid="asset-quick-view-title" className="min-w-0 truncate text-sm font-medium text-foreground">
          {heading}
        </span>
      ) : null}
      {badge}
      {reason ? (
        <span data-testid="asset-quick-view-reason" className="min-w-0 truncate text-xs text-muted-foreground">
          {reason}
        </span>
      ) : null}
    </div>
  );

  const view = describeStatus(summary);

  if (!summary || !view) {
    return (
      <div
        data-testid="asset-quick-view"
        className={cn("flex min-w-0 flex-col gap-2 px-2 py-1.5", className)}
        role="status"
        aria-busy={loading || undefined}
      >
        {loading ? (
          <>
            {header(<Skeleton className="h-5 w-14 rounded-full" />)}
            <div className="grid grid-cols-3 gap-2">
              {[0, 1, 2].map((index) => (
                <div key={index} className="flex flex-col gap-1">
                  <Skeleton className="h-3 w-10" />
                  <Skeleton className="h-4 w-14" />
                </div>
              ))}
            </div>
            <Skeleton className="h-3 w-32" />
            <span className="sr-only">{t("assetQuickView.loading")}</span>
          </>
        ) : (
          <>
            {heading ? header(null) : null}
            <span className="text-xs text-muted-foreground">
              {error ? t("assetQuickView.error") : t("assetQuickView.unknownDevice")}
            </span>
          </>
        )}
      </div>
    );
  }

  const readings = summary.readings ?? {};
  // Old values are "last measured", not current: muted, under their own caption.
  const outdated = view.stale || view.display === "offline";
  const alerting = !outdated && (view.display === "critical" || view.display === "warning");
  const trigger = alerting ? triggerMetric(summary.statusReason) : undefined;
  // An individual reading only carries its own age when it is clearly older (or newer) than the
  // last uplink; otherwise the one freshness line says it for all of them.
  const ownAge = (reading?: AssetReading) =>
    reading && differsByMoreThanAnHour(reading.timestamp, summary.lastUplinkAt)
      ? formatRelativeTime(reading.timestamp, language, reference)
      : undefined;

  const voltage = formatReading("batteryVoltage", readings.batteryVoltage, language);
  const stateOfCharge = formatReading("batteryStateOfCharge", readings.batteryStateOfCharge, language);
  const hasReadings = Object.values(readings).some(Boolean);

  const reason =
    summary.statusReason && !REASONS_SAID_BY_FRESHNESS.has(summary.statusReason)
      ? reasonText(summary.statusReason)
      : undefined;

  const freshness = freshnessLine(t, view, language, reference);

  const due = dueState(summary.nextCheck, reference);
  const nextCheckDate = formatDate(summary.nextCheck, language);

  return (
    <div
      data-testid="asset-quick-view"
      role="group"
      aria-label={t("assetQuickView.label")}
      className={cn("flex min-w-0 flex-col gap-2 px-2 py-1.5", className)}
    >
      {header(<AssetStatusBadge status={view.display} stale={view.stale} />, reason)}
      {hasReadings || view.display !== "noData" ? (
        <div
          data-testid="asset-quick-view-readings"
          data-outdated={outdated ? "true" : undefined}
          className={cn("rounded-md border px-2 py-1.5", outdated ? "border-dashed bg-transparent" : "bg-muted/40")}
        >
          {outdated ? (
            <div className="mb-0.5 text-[10px] font-medium uppercase leading-3 tracking-wide text-muted-foreground">
              {t("assetQuickView.lastMeasured")}
            </div>
          ) : null}
          <dl className="grid grid-cols-3 gap-2">
            <Stat
              testId="asset-quick-view-pressure"
              label={t("assetQuickView.pressure")}
              value={formatReading("pressure", readings.pressure, language)}
              age={ownAge(readings.pressure)}
              muted={outdated}
              alert={trigger === "pressure"}
            />
            <Stat
              testId="asset-quick-view-temperature"
              label={t("assetQuickView.temperatureShort")}
              value={formatReading("temperature", readings.temperature, language)}
              age={ownAge(readings.temperature)}
              muted={outdated}
              alert={trigger === "temperature"}
            />
            <Stat
              testId="asset-quick-view-battery"
              label={t("assetQuickView.battery")}
              value={voltage ?? stateOfCharge}
              extra={voltage ? stateOfCharge : undefined}
              age={ownAge(readings.batteryVoltage ?? readings.batteryStateOfCharge)}
              muted={outdated}
              alert={trigger === "batteryVoltage" || trigger === "batteryStateOfCharge"}
            />
          </dl>
        </div>
      ) : null}
      <div className="flex min-w-0 flex-col gap-0.5 text-[11px] leading-4">
        {freshness ? (
          freshness.stale ? (
            <span data-testid="asset-quick-view-uplink" className="min-w-0">
              <StaleHint text={freshness.text} />
            </span>
          ) : (
            <span data-testid="asset-quick-view-uplink" className="truncate text-muted-foreground">
              {freshness.text}
            </span>
          )
        ) : null}
        {nextCheckDate ? (
          <span
            data-testid="asset-quick-view-next-check"
            data-due={due ?? undefined}
            className={cn("truncate text-muted-foreground", due && DUE_STATE_CLASSES[due])}
          >
            {t("assetQuickView.nextCheck", { date: nextCheckDate })}
            {due === "overdue" ? ` · ${t("assetQuickView.overdue")}` : null}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** Quick view of the device with this Mongo `_id` (or business id). */
export default function AssetQuickView({
  assetId,
  title,
  className,
}: {
  assetId?: string;
  title?: ReactNode;
  className?: string;
}) {
  const { summaries, loading, error } = useAssetStatusSummary(assetId ? [assetId] : []);

  return (
    <AssetQuickViewBody
      summary={assetId ? summaries[assetId] : undefined}
      loading={loading}
      error={error}
      title={title}
      className={className}
    />
  );
}

// ---------------------------------------------------------------------------
// Table forms
// ---------------------------------------------------------------------------

/**
 * Badge over one short freshness line ("vor 6 Tagen" / "Offline seit 24.09., 07:24" /
 * "Stand 24.09., 07:31"). The badge's tooltip carries reason, temperature and the time of
 * the last transmission — the table has no column for them.
 */
export function AssetStatusCell({
  summary,
  loading,
  now,
}: {
  summary?: AssetStatusSummary | null;
  loading?: boolean;
  now?: number;
}) {
  const { t } = useTranslation();
  const language = useLanguage();
  const reasonText = useStatusReasonText();
  const view = describeStatus(summary);

  if (!summary || !view) {
    return loading ? (
      <div data-testid="asset-status-skeleton" className="flex flex-col gap-1" aria-busy="true">
        <Skeleton className="h-5 w-14 rounded-full" />
        <Skeleton className="h-3 w-16" />
      </div>
    ) : (
      <span className="text-xs text-muted-foreground">{DASH}</span>
    );
  }

  const reference = now ?? Date.now();
  const line = freshnessLine(t, view, language, reference, true);
  const reason = reasonText(summary.statusReason);
  const temperature = formatReading("temperature", summary.readings?.temperature, language);
  const seenAt = formatDateTime(view.lastSeenAt, language, reference);
  const tooltipLines = [
    reason,
    temperature ? `${t("assetQuickView.temperature")}: ${temperature}` : undefined,
    seenAt ? t("assetQuickView.lastTransmission", { time: seenAt }) : undefined,
  ].filter((part): part is string => !!part);

  return (
    <div data-testid="asset-status-cell" className="flex min-w-0 flex-col items-start gap-0.5">
      <AssetStatusBadge
        status={view.display}
        stale={view.stale}
        tooltip={
          tooltipLines.length > 0 ? (
            <div data-testid="asset-status-tooltip" className="flex flex-col gap-0.5">
              {tooltipLines.map((part) => (
                <span key={part}>{part}</span>
              ))}
            </div>
          ) : undefined
        }
      />
      {line ? (
        line.stale ? (
          <span data-testid="asset-status-freshness" title={line.text} className="max-w-full">
            <StaleHint text={line.text} />
          </span>
        ) : (
          <span
            data-testid="asset-status-freshness"
            title={line.text}
            className="max-w-full truncate text-[11px] leading-4 text-muted-foreground"
          >
            {line.text}
          </span>
        )
      ) : null}
    </div>
  );
}

export type ReadingColumn = "pressure" | "battery";

/** Numeric sort value of a reading column; `undefined` when missing. */
export function readingSortValue(summary: AssetStatusSummary | null | undefined, column: ReadingColumn) {
  const readings = summary?.readings ?? {};
  const reading =
    column === "pressure" ? readings.pressure : readings.batteryVoltage ?? readings.batteryStateOfCharge;
  return typeof reading?.value === "number" && Number.isFinite(reading.value) ? reading.value : undefined;
}

/**
 * One reading, right aligned: "15,4 bar", "3,53 V". Destructive when it is the value that
 * tripped the status; muted when the data is old.
 */
export function AssetReadingCell({
  summary,
  column,
  loading,
}: {
  summary?: AssetStatusSummary | null;
  column: ReadingColumn;
  loading?: boolean;
}) {
  const language = useLanguage();
  const view = describeStatus(summary);

  if (!summary) {
    return loading ? (
      <Skeleton data-testid="asset-reading-skeleton" className="ml-auto h-4 w-12" />
    ) : (
      <div className="text-right text-xs text-muted-foreground">{DASH}</div>
    );
  }

  const readings = summary.readings ?? {};
  const text =
    column === "pressure"
      ? formatReading("pressure", readings.pressure, language)
      : formatReading("batteryVoltage", readings.batteryVoltage, language) ??
        formatReading("batteryStateOfCharge", readings.batteryStateOfCharge, language);
  const outdated = !!view && (view.stale || view.display === "offline");
  const trigger =
    view && !outdated && (view.display === "critical" || view.display === "warning")
      ? triggerMetric(summary.statusReason)
      : undefined;
  const alert =
    column === "pressure"
      ? trigger === "pressure"
      : trigger === "batteryVoltage" || trigger === "batteryStateOfCharge";

  return (
    <div
      data-testid={`asset-reading-${column}`}
      data-alert={alert ? "true" : undefined}
      data-muted={outdated ? "true" : undefined}
      className={cn(
        "whitespace-nowrap text-right text-xs tabular-nums",
        !text || outdated ? "text-muted-foreground" : alert ? "font-semibold text-destructive" : "text-foreground",
      )}
    >
      {text ?? DASH}
    </div>
  );
}
